/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useState } from 'react'
import { MODULES } from '../../lib/scenario.ts'
import { TableDemo } from './TableDemo.tsx'
import { ChatPreview, DeckPreview, DocPreview } from './Previews.tsx'

/**
 * The four demo slots (LANDING_PAGE.md §4.4 / §5).
 *
 * HONESTY RULE: only monkeytab is a live component today. The other three are
 * labelled "preview" and say so in the UI — they render real scenario data and
 * hand over a real file, but they are not the shipping component. A page whose
 * whole thesis is trust cannot fake a demo. When each component's embed path is
 * confirmed, swap the preview for the real thing and flip `live` in scenario.ts.
 */

const PANELS: Record<string, () => React.JSX.Element> = {
  monkeytab: TableDemo,
  monkeydoc: DocPreview,
  monkeytalk: ChatPreview,
  monkeycard: DeckPreview,
}

export function DemoDeck() {
  const [active, setActive] = useState('monkeytab')
  const current = MODULES.find((m) => m.id === active) ?? MODULES[1]
  const Panel = PANELS[active] ?? TableDemo

  return (
    <div>
      <div
        className='scroll-x -mx-1 flex gap-2 px-1 pb-2'
        role='tablist'
        aria-label='Suite demos'
      >
        {MODULES.map((m) => {
          const selected = m.id === active
          return (
            <button
              key={m.id}
              role='tab'
              type='button'
              id={`tab-${m.id}`}
              aria-selected={selected}
              aria-controls={`panel-${m.id}`}
              onClick={() => setActive(m.id)}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                selected
                  ? 'border-[var(--brand)] bg-[var(--brand)] text-[var(--brand-ink)]'
                  : 'border-[var(--edge)] bg-[var(--surface-raised)] hover:bg-[var(--surface)]'
              }`}
            >
              {m.surface}
              <span className='ml-2 font-mono text-xs opacity-60'>{m.name}</span>
              {!m.live && (
                <span className='ml-2 rounded-full bg-[var(--surface-sunken)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--ink-muted)]'>
                  preview
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div
        role='tabpanel'
        id={`panel-${active}`}
        aria-labelledby={`tab-${active}`}
        className='mt-4 rounded-2xl border border-[var(--edge)] bg-[var(--surface-raised)] p-5 sm:p-7'
      >
        <div className='mb-5 flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h3 className='text-xl font-bold'>
              {current.surface}{' '}
              <span className='font-normal text-[var(--ink-muted)]'>— {current.familiar}</span>
            </h3>
            <p className='mt-1 max-w-xl text-sm text-[var(--ink-muted)]'>{current.edge}</p>
          </div>
          {current.live
            ? (
              <span className='shrink-0 rounded-full bg-[var(--accent)]/15 px-3 py-1 text-xs font-semibold text-[var(--accent)]'>
                ● Live component
              </span>
            )
            : (
              <span className='shrink-0 rounded-full bg-[var(--surface-sunken)] px-3 py-1 text-xs font-semibold text-[var(--ink-muted)]'>
                Preview — not the real component yet
              </span>
            )}
        </div>

        <Panel />
      </div>
    </div>
  )
}
